import React, { useEffect } from "react";
import { BrowserRouter, Route, Redirect, Switch } from "react-router-dom";
import { connect } from "react-redux";
import ErrorPage from "./commons/ErrorPage/ErrorPage";
import ProjectsLayout from "./commons/ProjectsLayout/ProjectsLayout";
import Login from "./pages/Login/Login";
import Embad from "./pages/Embed/EmbedSignup";
import { TENANT_ID, URL } from "./config/config";
import axios from "axios";
import { fetchTenantDetails } from "../src/store/tenantState";
import { fetchPartnerDetails } from "../src/store/partnerState";
import { fetchAgents } from "../src/store/partnerAgentsState";
import RazorpayAuthPage from "./pages/RazorpayAuth/RazorpayAuthPage";
import ErrorBoundary from "./commons/ErrorBoundary/ErrorBoundary";
import PartnerForm from "./pages/PartnerForm/partnerForm";

async function resizeImage(url, size = 192) {
  const img = await loadImage(url);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = size;
  canvas.height = size;

  ctx.drawImage(img, 0, 0, size, size);

  return canvas.toDataURL("image/png");
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

const createManifestIcons = async (faviconUrl, brandLogo, name) => {
  const icon192 = await resizeImage(faviconUrl, 192);
  const icon512 = await resizeImage(faviconUrl, 512);
  return [
    {
      src: faviconUrl,
      sizes: "16x16 24x24 32x32 64x64",
      type: "image/png",
      purpose: "any",
    },
    {
      src: icon192,
      type: "image/png",
      sizes: "192x192",
      purpose: "any maskable",
    },
    {
      src: icon512,
      type: "image/png",
      sizes: "512x512",
      purpose: "any maskable",
    },
  ];
};

const PrivateRoute = ({
  component,
  isAuthenticated,
  fetchTenantDetails,
  fetchPartnerDetails,
  fetchAgents,
  ...rest
}) => {
  useEffect(() => {
    fetchTenantDetails();
    fetchPartnerDetails();
    fetchAgents();
    if (TENANT_ID) {
      axios
        .post(URL + `/api/get-tenant-profile`, {
          tenantId: TENANT_ID,
        })
        .then(async (res) => {
          const { faviconUrl, name, brandLogo } = res.data.data;
          const icons = await createManifestIcons(faviconUrl, brandLogo, name);
          const dynamicManifest = {
            name: `${name} - Partner Dashboard`,
            short_name: `${name} - Partner Dashboard`,
            icons: icons,
            start_url: "/",
            display: "standalone",
            theme_color: "#000000",
            background_color: "#ffffff",
            screenshots: [
              {
                src: faviconUrl,
                sizes: "1280x720",
                type: "image/png",
                form_factor: "wide",
                label: `${name} Dashboard`,
              },
              {
                src: faviconUrl,
                sizes: "750x1334",
                type: "image/png",
                form_factor: "narrow",
                label: `${name} Dashboard`,
              },
            ],
          };
          const stringManifest = JSON.stringify(dynamicManifest);
          const blob = new Blob([stringManifest], { type: "application/json" });
          const manifestURL = window.URL.createObjectURL(blob);
          document
            .querySelector("#my-manifest-placeholder")
            .setAttribute("href", manifestURL);

          const existingIcons = document.querySelectorAll("link[rel*='icon']");
          existingIcons.forEach((icon) => icon.parentNode.removeChild(icon));

          const link = document.createElement("link");
          link.rel = "icon";
          link.type = "image/x-icon";
          link.href = `${faviconUrl}?v=${Date.now()}`;
          document.head.appendChild(link);

          document.title = `${name} - Partner Dashboard`;
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      const dynamicManifest = {
        name: `Aisensy - Partner Dashboard`,
        short_name: `Partner Dashboard`,
        icons: [
          {
            src: "/favicon.ico",
            sizes: "64x64 32x32 24x24 16x16",
            type: "image/x-icon",
          },
          {
            src: "android-chrome-192x192.png",
            type: "image/png",
            sizes: "192x192",
          },
          {
            src: "android-chrome-384x384.png",
            type: "image/png",
            sizes: "512x512",
          },
        ],
        start_url: ".",
        display: "standalone",
        theme_color: "#000000",
        background_color: "#ffffff",
      };
      const stringManifest = JSON.stringify(dynamicManifest);
      const blob = new Blob([stringManifest], { type: "application/json" });
      const manifestURL = window.URL.createObjectURL(blob);
      document
        .querySelector("#my-manifest-placeholder")
        .setAttribute("href", manifestURL);
      document.title = "AiSensy - Partner Dashboard";
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = "/favicon.ico";
    }
  }, []);

  return !isAuthenticated ? (
    <Redirect to={{ pathname: "/login" }} />
  ) : (
    <Route {...rest} component={component} />
  );
};
const PublicRoute = ({
  component,
  isAuthenticated,
  fetchTenantDetails,
  ...rest
}) => {
  useEffect(() => {
    fetchTenantDetails();
    if (TENANT_ID) {
      axios
        .post(URL + `/api/get-tenant-profile`, {
          tenantId: TENANT_ID,
        })
        .then(async (res) => {
          const { faviconUrl, brandLogo, name } = res.data.data;
          const icons = await createManifestIcons(faviconUrl, brandLogo, name);
          const dynamicManifest = {
            name: `${name} - Partner Dashboard`,
            short_name: `${name} - Partner Dashboard`,
            icons: icons,
            start_url: "/",
            display: "standalone",
            theme_color: "#000000",
            background_color: "#ffffff",
            screenshots: [
              {
                src: brandLogo,
                sizes: "1280x720",
                type: "image/png",
                form_factor: "wide",
                label: `${name} Dashboard`,
              },
              {
                src: brandLogo,
                sizes: "750x1334",
                type: "image/png",
                form_factor: "narrow",
                label: `${name} Dashboard`,
              },
            ],
          };

          const stringManifest = JSON.stringify(dynamicManifest);
          const blob = new Blob([stringManifest], { type: "application/json" });
          const manifestURL = window.URL.createObjectURL(blob);
          document
            .querySelector("#my-manifest-placeholder")
            .setAttribute("href", manifestURL);

          const existingIcons = document.querySelectorAll("link[rel*='icon']");
          existingIcons.forEach((icon) => icon.parentNode.removeChild(icon));

          const link = document.createElement("link");
          link.rel = "icon";
          link.type = "image/x-icon";
          link.href = `${faviconUrl}?v=${Date.now()}`;
          document.head.appendChild(link);

          document.title = `${name} - Partner Dashboard`;
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      const dynamicManifest = {
        name: `Aisensy - Partner Dashboard`,
        short_name: `Partner Dashboard`,
        icons: [
          {
            src: "/favicon.ico",
            sizes: "64x64 32x32 24x24 16x16",
            type: "image/x-icon",
          },
          {
            src: "android-chrome-192x192.png",
            type: "image/png",
            sizes: "192x192",
          },
          {
            src: "android-chrome-384x384.png",
            type: "image/png",
            sizes: "512x512",
          },
        ],
        start_url: ".",
        display: "standalone",
        theme_color: "#000000",
        background_color: "#ffffff",
      };
      const stringManifest = JSON.stringify(dynamicManifest);
      const blob = new Blob([stringManifest], { type: "application/json" });
      const manifestURL = window.URL.createObjectURL(blob);
      document
        .querySelector("#my-manifest-placeholder")
        .setAttribute("href", manifestURL);
      document.title = "AiSensy - Partner Dashboard";
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = "/favicon.ico";
    }
  }, []);
  return <Route {...rest} component={component} />;
};

const App = (props) => {
  return (
    <ErrorBoundary
      featureName="app"
      userAction="app_initialization"
      componentName="App"
    >
      <BrowserRouter>
        <Switch>
          <Route path="/:partnerId/embed/signup" component={Embad} />
          <Route path="/onboarding" component={PartnerForm} />

          <PublicRoute
            exact
            path="/login"
            component={Login}
            isAuthenticated={props.isAuthenticated}
            fetchTenantDetails={props.fetchTenantDetails}
          />
          <PrivateRoute
            path="/integrations/appstore/app/razorpay/auth"
            component={RazorpayAuthPage}
            isAuthenticated={props.isAuthenticated}
            fetchTenantDetails={props.fetchTenantDetails}
            fetchPartnerDetails={props.fetchPartnerDetails}
            fetchAgents={props.fetchAgents}
          />
          <PrivateRoute
            path="/"
            component={ProjectsLayout}
            isAuthenticated={props.isAuthenticated}
            fetchTenantDetails={props.fetchTenantDetails}
            fetchPartnerDetails={props.fetchPartnerDetails}
            fetchAgents={props.fetchAgents}
          />
          <Route component={ErrorPage} />
        </Switch>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default connect(
  (state) => ({
    isAuthenticated: state.login.isAuthenticated,
  }),
  { fetchTenantDetails, fetchPartnerDetails, fetchAgents },
)(App);
